<template>
  <div class="update-panel">
    <delete-role-confirmation v-if="getterPopupName === 'DeleteRoleConfirmation'"
                              :roleData="deleteOptions" />
    <role-confirmation v-if="getterPopupName === 'SaveRoleConfirmation'"
                      @updateRole="updateRole" />

    <div class="actions" v-if="isCreateAction">
      <button class="primary-btn" @click="triggerAction('Create')">
        <i class="fa fa-plus" />
        Create
      </button>
    </div>

    <div class="actions" v-if="manageAccess && roleEditable">
      <button class="primary-btn cancel" v-if="!disableData" @click="resetValues">
        <i class="fa fa-times" />
        Cancel
      </button>
      <button class="dark-btn" v-if="!disableData" @click="triggerAction('Update')">
        <i class="fa fa-save" />
        Save
      </button>
      <button class="dark-btn" v-else @click="disableData = false">
        <i class="fa fa-pencil-alt" />
        Edit
      </button>
      <button class="primary-btn delete" @click="deleteRole">
        <i class="fa fa-trash" />
        Delete
      </button>
    </div>

    <error-info :errorList="formErrors.role" class="main-error" />

    <md-field :class="{'input-error': $v.roleName.$error || formErrors.name.length}">
      <label>Name</label>
      <md-input v-model="roleName" :disabled="disableData" :ref="'RoleName'"
                @blur="$v.roleName.$touch()" />
    </md-field>
    <h6 class="error-message" v-if="$v.roleName.$error && !$v.roleName.required">Please enter Role Name</h6>
    <h6 class="error-message" v-else-if="$v.roleName.$error && !$v.roleName.alphaNumericSpace">Please enter a valid role name with alphanumeric characters &amp; space only</h6>
    <h6 class="error-message" v-else-if="$v.roleName.$error && !$v.roleName.maxLength">Please provide Role Name less than 100 characters</h6>
    <error-info v-else :errorList="formErrors.name" />

    <div class="sub-head">
      <h5>Privileges</h5>
      <h6 class="error-message" v-if="$v.privilegeList.$error">Please enable atleast one Privilege</h6>
      <error-info v-else :errorList="formErrors.privileges" />
    </div>

    <privilege-list :selectedPrivileges="privilegeList" :disableData="disableData"
                    @updateList="updatePrivilegeList" :reset="reset"
                    :self-va-enabled="selfVaEnabled" />
  </div>
</template>

<script>
import DeleteRoleConfirmation from '@/components/Roles/DeleteRoleConfirmation'
import PrivilegeList from '@/components/Roles/PrivilegeList'
import RoleConfirmation from '@/components/Roles/RoleConfirmation'
import ErrorInfo from '@/components/global/ErrorInfo'

import ApiServices from '@/services/api/roles'
import { openPopup, checkFormErrors, clearFormErrors } from '@/utils/helpers'
import { mapGetters } from 'vuex'
import { required, maxLength } from 'vuelidate/lib/validators'
import { alphaNumericSpace } from '@/utils/helpers/validators'

export default {
  name: 'RoleForm',
  components: { DeleteRoleConfirmation, PrivilegeList, RoleConfirmation, ErrorInfo },
  props: {
    roleFormOptions: Object,
    isCreateAction: Boolean
  },
  data () {
    return {
      disableData: true,
      roleId: null,
      roleName: null,
      privilegeList: [],
      roleEditable: false,
      manageAccess: false,
      deleteOptions: {},
      reset: false,
      backup: {
        name: null,
        privileges: []
      },
      formErrors: {
        role: [],
        name: [],
        privileges: []
      },
      selfVaEnabled: false
    }
  },
  validations: {
    roleName: {
      required,
      alphaNumericSpace,
      maxLength: maxLength(100)
    },
    privilegeList: {
      required
    }
  },
  watch: {
    roleFormOptions (newVal) {
      let data = newVal.data
      if (newVal.isCreateAction) {
        this.selfVaEnabled = data.self_va_enabled
        return
      }

      this.disableData = true
      this.roleName = data.name
      this.privilegeList = data.privileges
      this.roleEditable = data.editable
      this.roleId = data.id
      this.manageAccess = newVal.manageAccess
      this.selfVaEnabled = data.self_va_enabled

      this.backup = {
        name: this.roleName,
        privileges: this.privilegeList.slice()
      }
    },
    roleName () {
      this.formErrors.name = []
    }
  },
  computed: {
    ...mapGetters(['getterPopupName'])
  },
  beforeDestroy () {
    this.openPopup(null)
  },
  created () {
    if (this.isCreateAction) {
      this.disableData = false
    }
  },
  mounted () {
    if (this.isCreateAction) {
      this.$refs.RoleName.$el.focus()
    }
  },
  methods: {
    openPopup,
    checkFormErrors,
    clearFormErrors,
    triggerAction (type) {
      this.$v.$touch()
      if (!this.$v.$error && !checkFormErrors(this.formErrors)) {
        type === 'Create' ? this.createRole() : this.checkFormEdited()
      }
    },
    getParams () {
      return {
        name: this.roleName,
        privileges: this.privilegeList
      }
    },
    createRole () {
      let params = this.getParams()

      ApiServices.createRole(params).then(response => {
        if (response.success) {
          this.successToastMsg(response.message)
          this.$router.replace({name: 'RoleDetails', params: {id: response.data.id}})
        } else {
          this.errorToastMsg(response.message)
        }
      }).catch(error => {
        this.handleError(error)
      })
    },
    updateRole () {
      let params = this.getParams()

      ApiServices.updateRole(this.roleId, params).then(response => {
        if (response.success) {
          this.backup.privileges = this.privilegeList.slice()
          this.successToastMsg(response.message)
          this.openPopup(null)
        } else {
          this.errorToastMsg(response.message)
        }

        this.disableData = response.success
      }).catch(error => {
        this.handleError(error)
      })
    },
    checkFormEdited () {
      if (this.roleFormOptions.totalUsers && this.checkPrivilegesChanged()) {
        this.openPopup('SaveRoleConfirmation')
      } else {
        this.updateRole()
      }
    },
    checkPrivilegesChanged () {
      if (this.privilegeList.length === this.backup.privileges.length) {
        if (JSON.stringify(this.privilegeList) === JSON.stringify(this.backup.privileges)) {
          return false
        }
        return true
      }
      return true
    },
    handleError (response) {
      this.formErrors = {
        ...this.formErrors,
        ...response.body.reason
      }
    },
    deleteRole () {
      this.openPopup('DeleteRoleConfirmation')
      this.deleteOptions = {
        id: this.roleId,
        name: this.roleName
      }
    },
    resetValues () {
      this.$v.$reset()
      this.roleName = this.backup.name
      this.privilegeList = this.backup.privileges.slice()
      this.reset = !this.reset
      this.disableData = true
      this.clearFormErrors(this.formErrors)
    },
    updatePrivilegeList (val) {
      this.privilegeList = val
      this.formErrors.privileges = []
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/mixins.scss';

  .update-panel {
    background: $white;
    border-radius: 15px;
    margin-bottom: 15px;
    margin-top: 15px;

    .main-error {
      margin-bottom: 5px;
    }

    .actions {
      button {
        min-width: 100px;
        margin-left: 10px;

        &.delete {
          background: $error !important;
        }
        &.cancel {
          background: $grey-text !important;
        }

        i {
          margin-right: 5px;
        }
      }
    }

    .md-field {
      margin: 0 !important;
    }

    .sub-head {
      text-align: left;
      margin: 15px 0 5px;

      h5 {
        @include highlight-text($secondary, 14px);
        margin-right: 10px;
      }
    }
  }

  .desktop {
    .update-panel {
      width: 60%;
      padding: 20px;

      .actions {
        float: right;
      }

      .md-field {
        width: 45%;
      }
    }
  }

  .mobile, .tablet {
    .update-panel {
      padding: 10px;

      .actions {
        text-align: right;
      }

      .md-field {
        max-width: 350px;
      }
    }
  }
</style>
